import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils';

import JoinWaitlistIcon from './assets/join-waitlist-icon.png';

export interface WaitlistCardProps {
  courseName?: string;
  onCtaClick?: () => void;
  isLoading?: boolean;
  onClick?: () => void;
  forceLayout?: 'desktop' | 'mobile';
  className?: string;
  isWaitlistActive?: boolean;
}

const WaitlistCard = React.forwardRef<HTMLDivElement, WaitlistCardProps>(
  (
    {
      forceLayout,
      className,
      courseName,
      onCtaClick,
      isLoading = false,
      onClick,
      isWaitlistActive = false,
      ...props
    },
    ref
  ) => {
    const handleClick = onClick ?? onCtaClick;

    return (
      <div
        ref={ref}
        data-testid="waitlist-card"
        className={cn(
          'relative transition-all duration-200 focus:outline-none p-4 md:p-6 rounded-xl md:rounded-3xl w-full flex flex-row md:flex-col items-center md:justify-center',
          isWaitlistActive
            ? 'bg-magical-moonlight-s-cardbg'
            : 'bg-hot-sun-yellow-light-cardbg',
          className
        )}
        tabIndex={0}
        role="button"
        onClick={handleClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick?.();
          }
        }}
        {...props}
      >
        <>
          {/* Icon rendering */}
          <div className="flex justify-start items-center w-[4rem] h-[4rem] mr-4 md:mr-0 md:w-full">
            <img src={JoinWaitlistIcon} alt="Join Waitlist Icon" />
          </div>

          <div className="flex flex-col w-full">
            {/* Title */}
            <div className="text-left md:my-3 mt-0 mb-2">
              {isWaitlistActive ? (
                <h3 className="text-magical-moonlight-d4 text-sm">
                  Our team will help you find the best option
                </h3>
              ) : (
                <h3 className="text-magical-moonlight-d4 text-sm">
                  {`Can't find your `} <b>{courseName ?? 'course'}</b>{' '}
                  {' batch?'}
                </h3>
              )}
            </div>
            {/* CTA Button */}
            <div className="flex justify-start">
              <Button
                onClick={e => {
                  e.stopPropagation();
                  onCtaClick?.();
                }}
                disabled={isLoading}
                variant="outline"
                size="sm"
              >
                {isWaitlistActive ? 'Stay Waitlisted' : 'Join Waitlist'}
              </Button>
            </div>
          </div>
        </>

        {/* Shadow/Background element */}
        <div
          className={cn(
            'absolute opacity-60 -top-2 left-2 z-(--z-hide)',
            'bg-hot-sun-yellow-light-cardbg',
            (() => {
              if (forceLayout === 'desktop') {
                return 'w-56 h-48 rounded-3xl';
              }
              if (forceLayout === 'mobile') {
                return 'w-[calc(100%-16px)] h-auto rounded-xl';
              }
              // Default responsive behavior
              return 'w-56 h-48 rounded-3xl sm:w-56 sm:h-48 sm:rounded-3xl max-sm:w-[calc(100%-16px)] max-sm:h-auto max-sm:rounded-xl';
            })()
          )}
        />
      </div>
    );
  }
);

WaitlistCard.displayName = 'WaitlistCard';

export { WaitlistCard };
export default WaitlistCard;
